// Lyra(궁수) 임시 자세 표(c022 D4a): Lyra 전용 클립(Tripo → Mixamo, PC 게이트)이 오기 전까지 Kael 대검 클립 중 사격 자세에 가까운 것을 고른다.
// 고르는 기준: 몸을 비틀어 당겼다가 놓는 동작 = 2타 가로 휘두름(초승달 구간, 'shot'), 하늘로 쏘기 = 각성 모으기(powerup, 'sky'),
// 뒤로 빠지기 = 피격 반동(hurt, 'back'), 체공 사격 = 도약(leap, 'air'). 발사 틱(anchor)에 클립의 타격 프레임이 오게 맞춘다.
// 순수 데이터·함수(할당 없음): KaelModel이 view.heroId === 'lyra'일 때 읽는다. 카엘 경로에는 영향이 없다.
import { heroCombo } from '../shared/config';
import { heroSkills } from '../shared/skills/registry';

export type LyraPoseKind = 'shot' | 'sky' | 'back' | 'air';

/** 스킬 번호(1..13) → 자세 종류. 0번 칸은 기본 공격 */
export const LYRA_SKILL_POSE: readonly LyraPoseKind[] = [
  'shot', // 0 기본 공격 3타(빠른 2발 + 관통 강사)
  'shot', // 1 3연사
  'back', // 2 백스텝 사격: 뒤로 빠지는 반동 자세(게임이 몸을 4.5m 뒤로 옮긴다)
  'shot', // 3 관통 화살
  'shot', // 4 올려쏘기
  'sky', // 5 바람 화살비: 하늘로 쏜다
  'air', // 6 공중 연사: 도약 체공
  'shot', // 7 그물 덫
  'shot', // 8 폭발 화살
  'shot', // 9 회오리 화살
  'sky', // 10 유도 화살 폭풍: 화살이 떠오른다
  'air', // 11 천궁 강하: 높이 도약 후 내리꽂기
  'shot', // 12 전직 I(차지샷·지뢰밭): 서서 쏜다
  'shot', // 13 전직 II(관통 폭풍·덫 연쇄)
];

/**
 * 라이라 전용 몸(lyra_t1.glb)의 활 클립 구간(초): 자세 종류 → 클립·시작(s0)·발사(hit)·끝(s1).
 * 발사 프레임은 클립에서 오른손이 시위를 놓는 순간(qa/laneF-20261028 클립 시트로 확인). 전용 몸이 없으면 쓰지 않는다(카엘 대검 클립 대체).
 */
export interface LyraBowSegment { readonly clip: string; readonly s0: number; readonly hit: number; readonly s1: number }
export const LYRA_BOW_SEGMENTS: Readonly<Record<LyraPoseKind, LyraBowSegment>> = {
  shot: { clip: 'bow_shot', s0: 0, hit: 0.12, s1: 0.56 },
  sky: { clip: 'bow_sky', s0: 1.2, hit: 2.0, s1: 2.6 },
  back: { clip: 'bow_back', s0: 0, hit: 0.6, s1: 1.2 },
  air: { clip: 'bow_air', s0: 0.2, hit: 0.6, s1: 1.2 },
};

export interface LyraPoseTiming {
  kind: LyraPoseKind;
  /** 발사 틱(클립 타격 프레임을 이 틱에 둔다) */
  anchor: number;
  /** 동작 전체 틱 */
  total: number;
}

/** 스킬별 발사 틱·길이(로딩 때 한 번 계산): 첫 히트박스 시작 틱 = 첫 화살·장판이 나가는 틱. 전직 갈래(C10-6)마다 12·13번이 달라 표를 둘 둔다 */
const SKILL_TIMING: [LyraPoseTiming[], LyraPoseTiming[]] = [[], []];
for (const job of [0, 1] as const) for (const tl of heroSkills('lyra', job)) {
  let anchor = tl.duration;
  for (const hb of tl.hitboxes) if (hb.startTick < anchor) anchor = hb.startTick;
  if (anchor >= tl.duration) anchor = Math.round(tl.duration / 3);
  SKILL_TIMING[job][tl.id] = { kind: LYRA_SKILL_POSE[tl.id] ?? 'shot', anchor: Math.max(1, anchor), total: Math.max(2, tl.duration) };
}
const BASIC_TIMING: LyraPoseTiming[] = heroCombo('lyra').map((s) => ({ kind: 'shot' as const, anchor: Math.max(1, s.windup), total: Math.max(2, s.windup + s.active + s.recovery) }));
const FALLBACK: LyraPoseTiming = { kind: 'shot', anchor: 6, total: 24 };

/** 스킬 자세(없는 번호는 기본 'shot'). job = 전직 갈래(12·13번 길이가 갈래마다 다르다) */
export function lyraSkillPose(skillId: number, job: 0 | 1 = 0): LyraPoseTiming {
  return SKILL_TIMING[job === 1 ? 1 : 0][skillId] ?? FALLBACK;
}

/** 기본 공격 단계(0..2) 자세 */
export function lyraBasicPose(step: number): LyraPoseTiming {
  return BASIC_TIMING[step] ?? BASIC_TIMING[0] ?? FALLBACK;
}

/**
 * 시위 당김(0..1) 상태: 활을 쏘는 동작(공격·스킬) 중에는 DRAW_SEC 동안 가득 당기고, 화살이 나가면(release) 0으로 놓았다가 다시 당긴다.
 * 동작이 끝나면 RELAX_SEC 동안 풀린다. 렌더가 프레임마다 update(dt, aiming) → setLyraDraw(look, value)
 */
export class BowDraw {
  static readonly DRAW_SEC = 0.12;
  static readonly RELAX_SEC = 0.15;
  value = 0;
  update(dt: number, aiming: boolean): number {
    if (aiming) this.value = Math.min(1, this.value + dt / BowDraw.DRAW_SEC);
    else this.value = Math.max(0, this.value - dt / BowDraw.RELAX_SEC);
    return this.value;
  }
  release(): void { this.value = 0; }
}
